import { createStore } from '@frontend/store';
import { PreferenceDict, PreferenceKey, StoredPreference } from '../types';

type UserPreferencesStore = {
  prefs: Record<string, StoredPreference>;
  isHydrated: boolean;
};

export const useUserPreferencesStore = createStore<UserPreferencesStore>(
  () => ({
    prefs: {},
    isHydrated: false,
  }),
  {
    name: 'user-preferences-notification-store',
  }
);

export const userPreferencesActions = {
  setPreference: <K extends PreferenceKey>(
    id: K,
    value: PreferenceDict[K],
    updatedAt: string = new Date().toISOString()
  ) => {
    useUserPreferencesStore.setState((state) => {
      state.prefs[id] = { id, value, updatedAt };
    });
  },

  setPreferences: (prefs: Record<string, StoredPreference>) => {
    useUserPreferencesStore.setState((state) => {
      state.prefs = { ...state.prefs, ...prefs };
    });
  },

  syncFromMain: (prefs: Record<string, StoredPreference>) => {
    useUserPreferencesStore.setState((state) => {
      state.prefs = prefs;
      state.isHydrated = true;
    });
  },

  updateFromMain: (id: string, preference: StoredPreference) => {
    useUserPreferencesStore.setState((state) => {
      const existing = state.prefs[id];
      if (!existing || new Date(preference.updatedAt) > new Date(existing.updatedAt)) {
        state.prefs[id] = preference;
      }
    });
  },

  setHydrated: (hydrated: boolean) => {
    useUserPreferencesStore.setState((state) => {
      state.isHydrated = hydrated;
    });
  },
};

if (window.shell) {
  window.shell.on('user-preferences:sync-notifications', (_, payload) => {
    userPreferencesActions.syncFromMain(payload);
  });
} else {
  console.debug('Shell not available for user preferences sync');
}

export const updatePreferenceInMain = <K extends PreferenceKey>(id: K, value: PreferenceDict[K]): void => {
  const updatedAt = new Date().toISOString();

  userPreferencesActions.setPreference(id, value, updatedAt);

  if (window.shell) {
    window.shell
      .invoke('user-preferences:set', {
        id,
        value,
        updatedAt,
      })
      .catch((error) => {
        console.error('Failed to update preference in main app:', error);
      });
  }
};
